"use client";

/**
 * The clock behind a film: which beat is on and how far into it (0–1).
 * Counts real time in small steps, so pause, previous and next work, and a
 * restarted beat bumps `round` to replay its CSS. `loop` starts over after the
 * last beat; otherwise `onEnd` is called once. `stepMs` is how often it
 * looks (fast cuts on the beat need a finer step than the default 80 ms).
 */

import { useCallback, useEffect, useRef, useState } from "react";

export function useBeatClock(lengths: number[], { playing = true, loop = false, onEnd, stepMs = 80 }: { playing?: boolean; loop?: boolean; onEnd?: () => void; stepMs?: number } = {}) {
  const [beat, setBeat] = useState(0);
  const [round, setRound] = useState(0);
  const [progress, setProgress] = useState(0);
  const elapsed = useRef(0);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  const lengthsRef = useRef(lengths);
  lengthsRef.current = lengths;
  const n = lengths.length;

  const goTo = useCallback((i: number) => {
    elapsed.current = 0;
    setProgress(0);
    setBeat(((i % n) + n) % n);
    setRound(r => r + 1);
  }, [n]);

  useEffect(() => {
    if (!playing || n === 0) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      elapsed.current += now - last;
      last = now;
      const len = lengthsRef.current[beat];
      if (elapsed.current >= len) {
        if (beat === n - 1 && !loop) { window.clearInterval(id); endRef.current?.(); }
        else goTo(beat + 1);
      } else setProgress(elapsed.current / len);
    }, stepMs);
    return () => window.clearInterval(id);
  }, [playing, beat, goTo, loop, n, stepMs]);

  return { beat, progress, round, goTo };
}
